import { useRef, useState, useEffect } from 'react';

import {
  IconX,
  IconFiles,
  IconInfoCircle,
  IconFileTypeXls,
} from '@tabler/icons-react';

import CustomImage from '@/components/CustomImage';
import CustomModal from '@/components/CustomModal';
import { useFormatText } from '@/hooks/useFormatText';
import { downloadStudentTemplateCsv } from '@/lib/studentTemplate';
import { bulkUploadStudents } from '@/service/studentService';
import { type IBulkUploadResult } from '@/types/class';

import { BulkUploadReviewModal } from './BulkUploadReviewModal';

const resultHasIssues = (result: IBulkUploadResult): boolean =>
  (result.errors?.length ?? 0) > 0 ||
  (result.duplicates_in_file?.length ?? 0) > 0 ||
  (result.duplicates_in_db?.length ?? 0) > 0 ||
  (result.email_failures?.length ?? 0) > 0;

interface IBulkUploadStudentsModalProps {
  isOpen: boolean;
  onClose: () => void;
  classId: string;
  onSuccess?: () => void;
}

const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const ACCEPTED_MIME_TYPES = new Set([
  'text/csv',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]);
const ACCEPTED_EXTENSIONS = new Set(['csv', 'xls', 'xlsx']);

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const getExtension = (name: string): string => {
  const dot = name.lastIndexOf('.');
  return dot === -1 ? '' : name.slice(dot + 1).toLowerCase();
};

export const BulkUploadStudentsModal = ({
  isOpen,
  onClose,
  classId,
  onSuccess,
}: IBulkUploadStudentsModalProps) => {
  const { formatText } = useFormatText();
  const inputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string>('');
  const [isDragging, setIsDragging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadResult, setUploadResult] = useState<IBulkUploadResult | null>(
    null
  );

  const resetState = () => {
    setSelectedFile(null);
    setFileError('');
    setIsDragging(false);
    setUploadResult(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  useEffect(() => {
    resetState();
  }, [isOpen]);

  const validateFile = (file: File): string => {
    const extension = getExtension(file.name);

    const isValidExtension = ACCEPTED_EXTENSIONS.has(extension);
    const isValidMime = !file.type || ACCEPTED_MIME_TYPES.has(file.type);

    if (!isValidExtension || !isValidMime) {
      return formatText('classDetails.bulkUploadModal.errors.invalid-type');
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      return formatText('classDetails.bulkUploadModal.errors.max-size', {
        size: MAX_FILE_SIZE_MB,
      });
    }

    if (file.size === 0) {
      return formatText('classDetails.bulkUploadModal.errors.empty-file');
    }

    return '';
  };

  const handleFileChoice = (file: File | null | undefined) => {
    if (!file) return;
    const error = validateFile(file);

    if (error) {
      setSelectedFile(null);
      setFileError(error);
      if (inputRef.current) inputRef.current.value = '';
      return;
    }

    setFileError('');
    setSelectedFile(file);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleFileChoice(event.target.files?.[0]);
  };

  const handleDrop = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    handleFileChoice(event.dataTransfer.files?.[0]);
  };

  const handleDragOver = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!isDragging) setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFileError('');
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleClose = () => {
    if (isSubmitting) return;
    resetState();
    onClose();
  };

  const handleDownloadTemplate = async () => {
    try {
      await downloadStudentTemplateCsv();
    } catch (error) {
      console.error(error);
    }
  };

  const applyUploadResponse = (response: {
    status?: boolean;
    data?: unknown;
    message?: string;
  }) => {
    if (!response?.status) return;

    const result = response?.data as IBulkUploadResult | undefined;
    const hasStructuredResult = result && typeof result.total_rows === 'number';

    if (!hasStructuredResult) {
      onSuccess?.();
      resetState();
      onClose();
      return;
    }

    if (result.success_count > 0) onSuccess?.();

    if (resultHasIssues(result)) {
      setUploadResult(result);
    } else {
      resetState();
      onClose();
    }
  };

  const handleSubmit = async () => {
    if (!selectedFile) {
      setFileError(
        formatText('classDetails.bulkUploadModal.errors.file-required')
      );
      return;
    }

    if (!classId) {
      setFileError(
        formatText('classDetails.bulkUploadModal.errors.class-required')
      );
      return;
    }

    setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('class_id', classId);

      const response = await bulkUploadStudents(formData);
      applyUploadResponse(response);
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  if (uploadResult) {
    return (
      <BulkUploadReviewModal
        isOpen
        result={uploadResult}
        onClose={handleClose}
      />
    );
  }

  return (
    <CustomModal
      setIsOpen={handleClose}
      onClose={handleClose}
      position="right"
      isHeader
      heading={formatText('classDetails.bulkUploadModal.title')}
      isFooter
      cancelButtonProps={{
        label: formatText('common.actions.cancel'),
        onClick: handleClose,
        variant: 'link',
        disabled: isSubmitting,
      }}
      confirmButtonProps={{
        label: formatText('classDetails.bulkUploadModal.actions.upload'),
        onClick: handleSubmit,
        loading: isSubmitting,
        disabled: isSubmitting || !selectedFile,
      }}
      dataTestId="bulk-upload-students-modal"
    >
      <div
        className="w-full space-y-4"
        data-testid="bulk-upload-students-content"
      >
        {/* Dropzone OR Selected file */}
        {selectedFile ? (
          <div
            className="flex items-center justify-between gap-3 p-4 rounded-xl border border-neutral-light-200 bg-white"
            data-testid="bulk-upload-selected-file"
          >
            <div className="flex items-center gap-3 min-w-0">
              {getExtension(selectedFile?.name) === 'csv' ? (
                <CustomImage
                  src="/assets/svg/FileCSV.svg"
                  alt="CSV"
                  width={40}
                  height={40}
                  className="shrink-0"
                />
              ) : (
                <div className="shrink-0 w-10 h-10 flex items-center justify-center rounded-md bg-success-light-50 text-success-secondary">
                  <IconFileTypeXls size={24} />
                </div>
              )}
              <div className="flex flex-col gap-0.5 min-w-0">
                <p className="text-sm font-semibold text-neutral-primary truncate">
                  {selectedFile?.name}
                </p>
                <p className="text-xs text-neutral-secondary">
                  {formatFileSize(selectedFile?.size)}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleRemoveFile}
              className="shrink-0 w-8 h-8 flex items-center justify-center rounded-sm bg-neutral-light-50 text-neutral-pressed hover:bg-neutral-light-100 transition-colors"
              aria-label={formatText(
                'classDetails.bulkUploadModal.actions.remove-file'
              )}
              data-testid="bulk-upload-remove-file"
            >
              <IconX size={16} />
            </button>
          </div>
        ) : (
          <label
            htmlFor="bulk-upload-file-input"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            className={`flex flex-col items-center justify-center gap-3 px-6 py-8 rounded-xl border border-dashed cursor-pointer transition-colors ${
              isDragging
                ? 'border-brand-default bg-brand-light-50'
                : 'border-neutral-light-300 hover:border-brand-default hover:bg-neutral-light-50'
            }`}
            data-testid="bulk-upload-dropzone"
          >
            <div className="flex flex-col items-center gap-1 text-center">
              <p className="text-base font-semibold text-neutral-primary">
                {formatText('classDetails.bulkUploadModal.dropzone.title')}
              </p>
              <p className="text-sm text-neutral-secondary">
                {formatText('classDetails.bulkUploadModal.dropzone.subtitle', {
                  size: MAX_FILE_SIZE_MB,
                })}
              </p>
            </div>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-brand-default bg-white text-sm font-semibold text-brand-default">
              <IconFiles size={18} />
              {formatText('classDetails.bulkUploadModal.dropzone.browse')}
            </span>
          </label>
        )}

        <input
          id="bulk-upload-file-input"
          ref={inputRef}
          type="file"
          accept=".csv,.xls,.xlsx,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          className="hidden"
          onChange={handleInputChange}
          data-testid="bulk-upload-file-input"
        />

        {/* Info card with download template */}
        <div className="flex items-start gap-3 p-4 rounded-xl bg-brand-light-50">
          <div className="shrink-0 text-brand-default">
            <IconInfoCircle size={20} />
          </div>
          <div className="flex flex-col gap-2 min-w-0">
            <p className="text-sm text-neutral-primary leading-5">
              {formatText('classDetails.bulkUploadModal.info.required-fields')}
            </p>
            <p className="text-sm text-neutral-secondary leading-5">
              {formatText('classDetails.bulkUploadModal.info.help')}
            </p>
            <button
              type="button"
              onClick={handleDownloadTemplate}
              className="self-start text-sm font-semibold text-brand-default hover:text-brand-hover underline underline-offset-2 cursor-pointer"
              data-testid="download-template-button"
            >
              {formatText('classDetails.bulkUploadModal.info.download')}
            </button>
          </div>
        </div>

        {/* Error */}
        {fileError && (
          <p
            className="text-sm text-error-secondary"
            data-testid="bulk-upload-error"
          >
            {fileError}
          </p>
        )}
      </div>
    </CustomModal>
  );
};
